<template>
  <section class="lead-notes-panel">
    <header class="lead-notes-panel__heading">
      <strong>Notes</strong>
    </header>

    <form class="lead-notes-panel__form" @submit.prevent="createNote">
      <textarea
        v-model="comment"
        rows="2"
        placeholder="Add a note about this lead..."
      ></textarea>
      <button type="submit" :disabled="saving || !comment.trim()">
        {{ saving ? 'Saving...' : '+ Add note' }}
      </button>
      <p v-if="createError" role="alert">{{ createError }}</p>
    </form>

    <div v-if="loading && !notes.length" class="lead-notes-panel__loading" aria-label="Loading notes">
      <span></span>
      <span></span>
    </div>
    <div v-else-if="error && !notes.length" class="lead-notes-panel__state" role="alert">
      <p>Unable to load notes.</p>
      <button type="button" @click="loadNotes">Retry</button>
    </div>
    <div v-else-if="notes.length" class="lead-notes-panel__list">
      <article v-for="note in notes" :key="note.id">
        <header>
          <strong>{{ noteAuthor(note) }}</strong>
          <time
            v-tooltip="{ content: formatExactDate(note.created_at), placement: 'top', offset: 8 }"
          >
            {{ formatRelativeDate(note.created_at) }}
          </time>
        </header>
        <p>{{ note.comment }}</p>
      </article>
    </div>
  </section>
</template>

<script>
import { getLeadComments, postLeadComment } from '@/http'
import { formatExactDate, formatRelativeDate } from '@/views/crm/task-tracker/leadFlowDate'

export default {
  name: 'LeadNotesPanel',
  props: {
    leadId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      notes: [],
      comment: '',
      loading: false,
      saving: false,
      error: null,
      createError: '',
      requestGeneration: 0
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.loadNotes()
      }
    }
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    formatExactDate,
    formatRelativeDate,
    async loadNotes() {
      const leadId = this.leadId
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.loading = true
      this.error = null
      try {
        const { data } = await getLeadComments(leadId)
        if (generation !== this.requestGeneration || leadId !== this.leadId) return
        const results = Array.isArray(data) ? data : (data.results || [])
        this.notes = [...results].sort((left, right) => {
          return new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
        })
      } catch (error) {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.error = error
        }
      } finally {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.loading = false
        }
      }
    },
    async createNote() {
      const comment = this.comment.trim()
      if (!comment || this.saving) return
      this.saving = true
      this.createError = ''
      this.$emit('pending-change', true)
      try {
        await postLeadComment(this.leadId, { comment })
        await this.loadNotes()
        this.comment = ''
      } catch (error) {
        const data = error.response && error.response.data
        this.createError = data && data.message ? data.message : 'Unable to add note.'
      } finally {
        this.saving = false
        this.$emit('pending-change', false)
      }
    },
    noteAuthor(note) {
      const author = note.created_by || {}
      return author.full_name || author.name || author.username || 'Team member'
    },
    reset() {
      this.requestGeneration += 1
      this.notes = []
      this.comment = ''
      this.loading = false
      this.saving = false
      this.error = null
      this.createError = ''
    }
  }
}
</script>

<style scoped>
.lead-notes-panel {
  min-width: 0;
  padding: 16px;
  border: 1px solid #dfe4dd;
  border-radius: 7px;
  background: #fff;
}

.lead-notes-panel__heading,
.lead-notes-panel__list article header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lead-notes-panel__heading > strong {
  color: #657574;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-notes-panel time {
  color: #929d9b;
  font-size: 10px;
}

.lead-notes-panel__form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.lead-notes-panel__form textarea {
  min-width: 0;
  min-height: 58px;
  resize: vertical;
  padding: 9px 10px;
  border: 1px solid #d9dfd8;
  border-radius: 6px;
  color: #26393b;
  font: inherit;
  font-size: 11px;
  line-height: 16px;
}

.lead-notes-panel__form button {
  min-width: 92px;
  min-height: 34px;
  padding: 0 11px;
  border: 1px solid #b9cc9f;
  border-radius: 5px;
  background: #f7faf3;
  color: #528116;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.lead-notes-panel__form button:disabled {
  cursor: default;
  opacity: .55;
}

.lead-notes-panel__form > p {
  grid-column: 1 / -1;
  margin: 0;
  color: #b85049;
  font-size: 10px;
}

.lead-notes-panel__list {
  display: grid;
  gap: 9px;
  margin-top: 14px;
}

.lead-notes-panel__list article {
  min-width: 0;
  padding: 10px 11px;
  border-left: 3px solid #8eb951;
  border-radius: 0 5px 5px 0;
  background: #f6f8f3;
}

.lead-notes-panel__list article strong {
  overflow: hidden;
  color: #4a5c5d;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-notes-panel__list article p {
  margin: 6px 0 0;
  color: #48595a;
  font-size: 11px;
  line-height: 16px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.lead-notes-panel__state {
  margin: 14px 0 0;
  color: #8a9695;
  font-size: 11px;
}

.lead-notes-panel__state button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #55940a;
  font-weight: 700;
  cursor: pointer;
}

.lead-notes-panel__loading {
  display: grid;
  gap: 8px;
  margin-top: 14px;
}

.lead-notes-panel__loading span {
  height: 55px;
  border-radius: 5px;
  background: #eef1ec;
}

@media (max-width: 600px) {
  .lead-notes-panel__form {
    grid-template-columns: 1fr;
  }

  .lead-notes-panel__form button {
    justify-self: end;
  }
}
</style>
